import { artRgbColor } from "../../lib/schedule-art/art-color";
import { artFontWeight } from "../../lib/schedule-art/art-fonts";
import type Konva from "konva";
import type PptxGenJS from "pptxgenjs";
import { PIXELS_PER_INCH } from "./powerpoint-scene";

const POINTS_PER_PIXEL = 72 / PIXELS_PER_INCH;

function textColor(fill: string) {
  const rgb = artRgbColor(fill);
  if (!rgb) throw new Error("Unsupported PowerPoint text color");
  const channels = [rgb.r, rgb.g, rgb.b];
  const hex = channels.map((channel) =>
    channel.toString(16).padStart(2, "0"),
  );
  return {
    color: hex.join("").toUpperCase(),
    transparency: (1 - (rgb.alpha ?? 1)) * 100,
  };
}

export function powerPointTextOptions(
  text: Konva.Text,
  name: string,
): PptxGenJS.TextPropsOptions {
  // Office can need more space than the browser's tightly measured width.
  // Reserve 20% extra width, with at least one font-size of trailing room.
  const trailingSpace = Math.max(text.width() * 0.2, text.fontSize());
  const width = Math.ceil(text.width() + trailingSpace);
  const height = text.height();
  // Use the expanded box's center so rotation preserves the text's origin.
  // Konva rotates around the element's origin; Office rotates around its center.
  const center = text.getAbsoluteTransform().point({ x: width / 2, y: height / 2 });
  const style = text.fontStyle().toLowerCase();
  const bold = artFontWeight(style) >= 600;
  const rotation = ((text.getAbsoluteRotation() % 360) + 360) % 360;
  const fill = text.fill();
  if (typeof fill !== "string") throw new Error("Unsupported PowerPoint text fill");

  return {
    objectName: name,
    isTextBox: true,
    x: (center.x - width / 2) / PIXELS_PER_INCH,
    y: (center.y - height / 2) / PIXELS_PER_INCH,
    w: Math.max(1, width) / PIXELS_PER_INCH,
    h: Math.max(1, height) / PIXELS_PER_INCH,
    fontFace: text.fontFamily(),
    fontSize: text.fontSize() * POINTS_PER_PIXEL,
    bold,
    italic: style.includes("italic"),
    ...textColor(fill),
    rotate: rotation,
    margin: 0,
    valign: "top",
    align: "left",
    breakLine: false,
    paraSpaceBefore: 0,
    paraSpaceAfter: 0,
    lineSpacing: text.lineHeight() * text.fontSize() * POINTS_PER_PIXEL,
    charSpacing: text.letterSpacing() * POINTS_PER_PIXEL,
    // Export the extra width directly instead of relying on Office to resize
    // the box on opening. Keep its font size and padded bounds fixed.
    fit: "none",
    wrap: false,
  };
}

export function powerPointTextLayers(
  text: Konva.Text,
  name: string,
): PptxGenJS.TextPropsOptions[] {
  const options = powerPointTextOptions(text, name);
  if (!text.hasStroke()) return [options];

  const strokeColor = text.stroke();
  if (typeof strokeColor !== "string") {
    throw new Error("Unsupported PowerPoint text stroke color");
  }
  const stroke = textColor(strokeColor);
  const outline = {
    color: stroke.color,
    size: text.strokeWidth() * POINTS_PER_PIXEL,
  };
  if (!text.fillAfterStrokeEnabled()) return [{ ...options, outline }];

  // The fill-only front copy covers the inner half of the doubled outline.
  // Keep both copies' geometry identical, including the rotation center.
  return [
    { ...options, objectName: `${name} - Outline`, outline },
    { ...options, objectName: `${name} - Fill` },
  ];
}
